/**
 * Design System Playground
 *
 * Visual showcase of all design tokens and components in both light and dark mode.
 * Use this to verify the design system works correctly before building pages.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Sun, Moon, Check, X, AlertTriangle, Info, ChevronRight,
  Search, Plus, Send, Settings, User, Bell, Shield, Bot,
  BookOpen, Zap, BarChart3, Wrench, MessageSquare,
  MoreVertical, Pencil, Trash2, Archive, Share2, Download,
  Copy, Bookmark, FolderOpen, Star, Pin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme-provider';
import { ArgusEye } from '@/components/icons';
import { StatusDot } from '@/components/shared/status-dot';
import { ScoreBar } from '@/components/shared/score-bar';
import { requireReferencePlaygroundAccess } from '@/lib/reference-playground-gate';
// Production chat components
import {
  ThinkingBlock,
  ToolCallCard,
  ConfidenceBadge,
  MarkdownRenderer,
  MessageActions,
} from '@/components/chat';

export const Route = createFileRoute('/playground')({
  beforeLoad: requireReferencePlaygroundAccess,
  component: DesignPlayground,
});

function DesignPlayground() {
  const { mode, toggleTheme } = useTheme();
  const darkMode = mode === 'dark';

  return (
    <div className="h-full overflow-auto bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Design System Playground</h1>
            <p className="text-sm text-muted-foreground">ArgusAI Component Library — {darkMode ? 'Dark' : 'Light'} Mode</p>
          </div>
          <Button variant="outline" size="sm" onClick={toggleTheme} className="gap-2">
            {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">

        {/* ── Color Palette ──────────────────────────────────────── */}
        <Section title="Color Palette" description="OKLCH-based design tokens for light and dark mode">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <ColorSwatch name="Background" className="bg-background" textClass="text-foreground" />
            <ColorSwatch name="Foreground" className="bg-foreground" textClass="text-background" />
            <ColorSwatch name="Primary" className="bg-primary" textClass="text-primary-foreground" />
            <ColorSwatch name="Secondary" className="bg-secondary" textClass="text-secondary-foreground" />
            <ColorSwatch name="Muted" className="bg-muted" textClass="text-muted-foreground" />
            <ColorSwatch name="Accent" className="bg-accent" textClass="text-accent-foreground" />
            <ColorSwatch name="Card" className="bg-card" textClass="text-card-foreground" border />
            <ColorSwatch name="Popover" className="bg-popover" textClass="text-popover-foreground" border />
            <ColorSwatch name="Destructive" className="bg-destructive" textClass="text-destructive-foreground" />
            <ColorSwatch name="Border" className="bg-border" textClass="text-foreground" />
            <ColorSwatch name="Input" className="bg-input" textClass="text-foreground" />
            <ColorSwatch name="Ring" className="bg-ring" textClass="text-primary-foreground" />
          </div>

          <h4 className="text-sm font-semibold mt-6 mb-3">Sidebar Colors</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ColorSwatch name="Sidebar" className="bg-sidebar" textClass="text-sidebar-foreground" border />
            <ColorSwatch name="Sidebar Primary" className="bg-sidebar-primary" textClass="text-sidebar-primary-foreground" />
            <ColorSwatch name="Sidebar Accent" className="bg-sidebar-accent" textClass="text-sidebar-accent-foreground" />
            <ColorSwatch name="Sidebar Border" className="bg-sidebar-border" textClass="text-foreground" />
          </div>

          <h4 className="text-sm font-semibold mt-6 mb-3">Semantic Colors</h4>
          <div className="grid grid-cols-3 gap-3">
            <ColorSwatch name="Success" className="bg-success" textClass="text-white" />
            <ColorSwatch name="Warning" className="bg-warning" textClass="text-white" />
            <ColorSwatch name="Info" className="bg-info" textClass="text-white" />
          </div>
        </Section>

        {/* ── Typography ─────────────────────────────────────────── */}
        <Section title="Typography" description="Font scale and weights">
          <div className="space-y-4">
            <p className="text-4xl font-semibold">Heading 1 — 4xl Semibold</p>
            <p className="text-3xl font-semibold">Heading 2 — 3xl Semibold</p>
            <p className="text-2xl font-semibold">Heading 3 — 2xl Semibold</p>
            <p className="text-xl font-medium">Heading 4 — xl Medium</p>
            <p className="text-lg font-medium">Heading 5 — lg Medium</p>
            <p className="text-base">Body — base (16px)</p>
            <p className="text-sm">Small — sm (14px)</p>
            <p className="text-xs">Extra small — xs (12px)</p>
            <p className="text-sm text-muted-foreground">Muted text — secondary information</p>
            <p className="text-sm text-primary font-medium">Primary text — links and actions</p>
            <p className="text-sm text-destructive">Destructive text — errors and warnings</p>
          </div>
        </Section>

        {/* ── Buttons ────────────────────────────────────────────── */}
        <Section title="Buttons" description="All button variants and sizes">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="link">Link</Button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon" aria-label="Add (icon size example)" title="Icon size"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Button disabled>Disabled</Button>
              <Button className="gap-2"><Send className="h-4 w-4" /> With Icon</Button>
              <Button variant="outline" className="gap-2"><Settings className="h-4 w-4" /> Settings</Button>
            </div>
          </div>
        </Section>

        {/* ── Badges ─────────────────────────────────────────────── */}
        <Section title="Badges" description="Status indicators and labels">
          <div className="flex flex-wrap gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge className="bg-success text-white">Success</Badge>
            <Badge className="bg-warning text-white">Warning</Badge>
            <Badge className="bg-primary/10 text-primary border-primary/20">Purple Soft</Badge>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="outline" className="gap-1"><Check className="h-3 w-3" /> Compliant</Badge>
            <Badge variant="outline" className="gap-1 text-destructive border-destructive/30"><X className="h-3 w-3" /> Non-Compliant</Badge>
            <Badge variant="outline" className="gap-1 text-warning border-warning/30"><AlertTriangle className="h-3 w-3" /> Partial</Badge>
            <Badge className="bg-primary text-primary-foreground">Active</Badge>
            <Badge variant="secondary">Draft</Badge>
            <Badge variant="outline">Archived</Badge>
          </div>
        </Section>

        {/* ── Form Inputs ────────────────────────────────────────── */}
        <Section title="Form Inputs" description="Text inputs, textareas, toggles">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Text Input</label>
                <Input placeholder="Enter text..." />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">With Icon</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search..." className="pl-9" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Disabled</label>
                <Input placeholder="Disabled..." disabled />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Textarea</label>
                <Textarea placeholder="Type your message..." rows={3} />
              </div>
              <div className="flex items-center gap-3">
                <Switch />
                <span className="text-sm">Toggle switch</span>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Progress</label>
                <Progress value={67} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">67% complete</p>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Cards ──────────────────────────────────────────────── */}
        <Section title="Cards & Surfaces" description="Content containers with depth layers">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Standard Card</CardTitle>
                <CardDescription>Default card with border</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Card content sits on the card surface, slightly elevated from background.</p>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-base text-primary">Active / Selected</CardTitle>
                <CardDescription>Purple-tinted active state</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Used for selected items, active states, and focus indicators.</p>
              </CardContent>
            </Card>

            <Card className="border-destructive/20 bg-destructive/5">
              <CardHeader>
                <CardTitle className="text-base text-destructive">Error / Alert</CardTitle>
                <CardDescription>Red-tinted alert state</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Used for errors, warnings, and critical alerts.</p>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* ── Icon Rail Preview ───────────────────────────────────── */}
        <Section title="Icon Rail (Navigation)" description="Proposed 9-item navigation rail">
          <div className="flex gap-6">
            {/* Rail */}
            <div className="w-[72px] bg-sidebar border border-sidebar-border rounded-2xl p-2 flex flex-col items-center gap-1">
              {[
                { icon: MessageSquare, label: 'Ask Argus', active: true },
                { icon: Shield, label: 'Approvals' },
                { icon: Wrench, label: 'Workspace' },
                { icon: BookOpen, label: 'Library' },
                { icon: Zap, label: 'Automate' },
                { icon: Bot, label: 'Models' },
                { icon: BarChart3, label: 'Insights' },
                { icon: Shield, label: 'Trust' },
                { icon: Settings, label: 'Admin' },
              ].map(({ icon: Icon, label, active }) => (
                <button
                  key={label}
                  className={cn(
                    "flex flex-col items-center gap-0.5 w-full py-2 rounded-xl text-[10px] font-medium transition-colors",
                    active
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="leading-tight">{label}</span>
                </button>
              ))}
              <Separator className="my-1" />
              <button className="flex flex-col items-center gap-0.5 w-full py-2 rounded-xl text-[10px] font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50">
                <User className="h-5 w-5" />
                <span>Profile</span>
              </button>
            </div>

            {/* Content preview */}
            <div className="flex-1 border border-border/40 rounded-2xl p-4 bg-card">
              <p className="text-sm text-muted-foreground">Content area changes based on active rail item. Horizontal menu tabs appear here.</p>
              <div className="mt-3">
                <Tabs defaultValue="conversations">
                  <TabsList>
                    <TabsTrigger value="conversations">Conversations</TabsTrigger>
                    <TabsTrigger value="projects">Projects</TabsTrigger>
                    <TabsTrigger value="approvals">Approvals</TabsTrigger>
                  </TabsList>
                  <TabsContent value="conversations" className="mt-3">
                    <p className="text-sm text-muted-foreground">Conversation list would appear here with search, folders, and date grouping.</p>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Chat Components ────────────────────────────────────── */}
        <Section title="Chat Components" description="Message bubbles, tool cards, confidence badges">
          <div className="max-w-2xl space-y-4">
            {/* User message */}
            <div className="flex justify-end">
              <div className="bg-muted rounded-2xl px-4 py-3 max-w-md">
                <p className="text-sm">What is the current vibration reading on pump P-401?</p>
              </div>
            </div>

            {/* Tool call card */}
            <Card className="border-border/40">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">get_current_time</span>
                  </div>
                  <Badge variant="outline" className="text-success border-success/30 gap-1">
                    <Check className="h-3 w-3" /> Success
                  </Badge>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  <span>0ms</span>
                  <span className="mx-2">·</span>
                  <Badge variant="secondary" className="text-[10px] h-5">Auto-approved</Badge>
                  <span className="mx-2">·</span>
                  <span>Source: Built-in</span>
                </div>
              </CardContent>
            </Card>

            {/* Assistant message */}
            <div className="space-y-2">
              <p className="text-sm">Based on the latest telemetry data, the vibration reading on pump P-401 is <strong>4.2 mm/s</strong>, recorded at 2:30 PM UTC today.</p>
              <p className="text-sm">This is within the normal operating range (0-6 mm/s). However, I notice it has been trending upward over the past week. [Source 1]</p>

              {/* Confidence badge */}
              <div className="flex items-center gap-3 mt-3">
                <Badge className="bg-success/10 text-success border border-success/20">
                  85 — High confidence
                </Badge>
                <div className="flex gap-1">
                  <button className="p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Copy" title="Copy">
                    <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  </button>
                  <button className="p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Thumbs up" title="Thumbs up">
                    <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" /></svg>
                  </button>
                  <button className="p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Thumbs down" title="Thumbs down">
                    <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" /></svg>
                  </button>
                </div>
              </div>

              {/* Follow-up suggestions */}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" className="text-xs rounded-full h-7">
                  Show vibration trend for the past week
                </Button>
                <Button variant="outline" size="sm" className="text-xs rounded-full h-7">
                  What is the maintenance schedule for P-401?
                </Button>
              </div>
            </div>
          </div>
        </Section>

        {/* ── Production Chat Components ───────────────────────── */}
        <Section title="Chat Components (Production)" description="These are the STANDARD components — use these everywhere, never recreate">

          {/* ArgusEye */}
          <h4 className="text-sm font-semibold mb-3">ArgusEye — Assistant Avatar</h4>
          <div className="flex items-center gap-6 mb-8">
            {(['idle', 'thinking', 'speaking', 'alert', 'listening', 'sleeping'] as const).map(state => (
              <div key={state} className="text-center">
                <ArgusEye state={state} className="w-10 h-10 mx-auto" />
                <p className="text-[10px] text-muted-foreground mt-1">{state}</p>
              </div>
            ))}
          </div>

          {/* ToolCallCard */}
          <h4 className="text-sm font-semibold mb-3">ToolCallCard — Tool Execution Display</h4>
          <div className="space-y-2 max-w-2xl mb-8">
            <ToolCallCard toolName="iq.telemetry.getTimeSeries" args={{ assetTag: 'C-201', parameter: 'vibration_rms', period: '30d' }} result={{ dataPoints: 720, latest: 4.2 }} durationMs={245} status="success" policyAction="auto_approve" />
            <ToolCallCard toolName="iq.cmms.createWorkOrder" args={{ assetTag: 'C-201', type: 'CM', priority: 'high' }} result={null} durationMs={0} status="approval_required" policyAction="require_approval" />
            <ToolCallCard toolName="ext.sap.searchParts" args={{ query: 'SKF 6205' }} result={null} durationMs={0} status="pending" policyAction="auto_approve" />
            <ToolCallCard toolName="iq.asset.getDetails" args={{ id: 'invalid' }} result={null} durationMs={120} status="failed" policyAction="auto_approve" error="Asset not found" />
          </div>

          {/* ConfidenceBadge */}
          <h4 className="text-sm font-semibold mb-3">ConfidenceBadge — Response Confidence</h4>
          <div className="flex items-center gap-4 mb-8">
            <ConfidenceBadge score={95} />
            <ConfidenceBadge score={72} />
            <ConfidenceBadge score={35} />
          </div>

          {/* ThinkingBlock */}
          <h4 className="text-sm font-semibold mb-3">ThinkingBlock — Model Reasoning Display</h4>
          <div className="max-w-2xl mb-8">
            <ThinkingBlock content="I need to analyze the vibration data for compressor C-201. The user is asking about trends over 30 days, so I should use the telemetry API to fetch time series data. Let me also check the maintenance manual for acceptable vibration thresholds according to ISO 10816-3." />
          </div>

          {/* MessageActions */}
          <h4 className="text-sm font-semibold mb-3">MessageActions — Always-Visible Action Bar</h4>
          <div className="max-w-2xl mb-8 p-4 bg-card rounded-lg border">
            <p className="text-sm mb-3">Sample assistant response text here. The action bar below should always be visible.</p>
            <TooltipProvider>
              <MessageActions role="assistant" content="Sample response" messageId="demo-1" metadata={{ model: 'qwen2.5:7b', totalTokens: 245, latencyMs: 2800, ragSourceCount: 3, confidenceScore: 91 }} />
            </TooltipProvider>
          </div>

          {/* MarkdownRenderer */}
          <h4 className="text-sm font-semibold mb-3">MarkdownRenderer — Rich Content Display</h4>
          <div className="max-w-2xl mb-8 p-4 bg-card rounded-lg border">
            <MarkdownRenderer content={SAMPLE_MARKDOWN} />
          </div>

          {/* StatusDot */}
          <h4 className="text-sm font-semibold mb-3">StatusDot — Health Indicator</h4>
          <div className="flex items-center gap-6 mb-8">
            {(['healthy', 'active', 'degraded', 'pending', 'down', 'inactive', 'unknown'] as const).map(status => (
              <div key={status} className="flex items-center gap-2">
                <StatusDot status={status} />
                <span className="text-xs">{status}</span>
              </div>
            ))}
          </div>

          {/* ScoreBar */}
          <h4 className="text-sm font-semibold mb-3">ScoreBar — Metric Visualization</h4>
          <div className="max-w-sm space-y-2 mb-8">
            <ScoreBar value={95} label="Faithfulness" />
            <ScoreBar value={72} label="Relevancy" />
            <ScoreBar value={45} label="Low Score" />
            <ScoreBar value={0.91} max={1} label="Normalized" />
          </div>

          {/* Chat Row with Ellipsis — THE STANDARD PATTERN */}
          <h4 className="text-sm font-semibold mb-3">Chat Row with Ellipsis Menu — Standard Pattern</h4>
          <p className="text-xs text-muted-foreground mb-3">This is the reference pattern for conversation rows. The three-dot menu must ALWAYS be visible.</p>
          <div className="max-w-sm space-y-1 p-3 bg-card rounded-lg border mb-8">
            <RefChatRow title="LOTO Procedure for Pump P-401" time="2h" folder="Safety & Compliance" folderColor="#ef4444" active />
            <RefChatRow title="Vibration analysis for compressor C-201" time="15m" folder="Maintenance" folderColor="#f59e0b" />
            <RefChatRow title="Calculate pump flow rate conversion" time="3h" />
            <RefChatRow title="Work order planning for bearing replacement on compressor" time="1d" folder="Maintenance" folderColor="#f59e0b" />
          </div>

          {/* Dropdown Menu Pattern */}
          <h4 className="text-sm font-semibold mb-3">Dropdown Menu — Standard Pattern</h4>
          <div className="flex gap-4 mb-8">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">Open Menu</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem className="gap-2"><Share2 className="h-4 w-4" /> Share</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Download className="h-4 w-4" /> Download</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Pencil className="h-4 w-4" /> Rename</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2"><Bookmark className="h-4 w-4" /> Pin</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Copy className="h-4 w-4" /> Clone</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><FolderOpen className="h-4 w-4" /> Move to Folder</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Star className="h-4 w-4" /> Add to Project</DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Section>

        {/* ── Surface Depth ──────────────────────────────────────── */}
        <Section title="Surface Depth (Dark Mode)" description="Layered surfaces for visual hierarchy">
          <div className="grid grid-cols-5 gap-3">
            <div className="p-4 rounded-xl bg-background border border-border/40 text-center">
              <p className="text-xs font-medium">L0</p>
              <p className="text-[10px] text-muted-foreground">Background</p>
            </div>
            <div className="p-4 rounded-xl bg-sidebar border border-sidebar-border text-center">
              <p className="text-xs font-medium">L1</p>
              <p className="text-[10px] text-muted-foreground">Sidebar</p>
            </div>
            <div className="p-4 rounded-xl bg-card border border-border/40 text-center">
              <p className="text-xs font-medium">L2</p>
              <p className="text-[10px] text-muted-foreground">Card</p>
            </div>
            <div className="p-4 rounded-xl bg-muted text-center">
              <p className="text-xs font-medium">L3</p>
              <p className="text-[10px] text-muted-foreground">Muted</p>
            </div>
            <div className="p-4 rounded-xl bg-input text-center">
              <p className="text-xs font-medium">L4</p>
              <p className="text-[10px] text-muted-foreground">Input</p>
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}

// ── Helper Components ─────────────────────────────────────────────────

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold mb-1">{title}</h2>
      <p className="text-sm text-muted-foreground mb-4">{description}</p>
      {children}
    </section>
  );
}

function ColorSwatch({ name, className, textClass, border }: { name: string; className: string; textClass: string; border?: boolean }) {
  return (
    <div className={cn("rounded-xl p-4 text-center", className, textClass, border && "border border-border/40")}>
      <p className="text-xs font-semibold">{name}</p>
    </div>
  );
}

// ── Reference Chat Row (the standard pattern) ──────────────────────────

function RefChatRow({ title, time, folder, folderColor, active }: {
  title: string; time: string; folder?: string; folderColor?: string; active?: boolean;
}) {
  return (
    <div className={cn(
      'rounded-lg cursor-pointer transition-colors px-2 py-2',
      active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50 text-foreground'
    )}>
      <div className="flex items-center gap-1.5">
        <p className="flex-1 min-w-0 truncate text-sm">{title}</p>
        <span className="text-[10px] text-foreground/40 shrink-0 tabular-nums">{time}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center justify-center w-8 h-8 rounded-md border border-border/50 bg-muted hover:bg-accent text-foreground shrink-0" aria-label="More actions" title="More actions">
              <span className="text-lg leading-none">⋮</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4} className="w-48 z-50">
            <DropdownMenuItem className="gap-2"><Share2 className="h-4 w-4" /> Share</DropdownMenuItem>
            <DropdownMenuItem className="gap-2"><Download className="h-4 w-4" /> Download</DropdownMenuItem>
            <DropdownMenuItem className="gap-2"><Pencil className="h-4 w-4" /> Rename</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2"><Bookmark className="h-4 w-4" /> Pin</DropdownMenuItem>
            <DropdownMenuItem className="gap-2"><Copy className="h-4 w-4" /> Clone</DropdownMenuItem>
            <DropdownMenuItem className="gap-2"><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {folder && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md"
            style={{ backgroundColor: (folderColor ?? '#888') + '20', color: folderColor }}>
            {folder}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Sample Markdown for MarkdownRenderer ────────────────────────────────

const SAMPLE_MARKDOWN = `## Vibration Analysis — Compressor C-201

The overall vibration level is **4.2 mm/s RMS**, which is within the **Alert** threshold.

### 30-Day Trend

| Period | Vibration (mm/s RMS) | Change |
|--------|---------------------|--------|
| 30 days ago | 2.8 | baseline |
| 20 days ago | 3.1 | +10.7% |
| Current | **4.2** | +16.7% |

### Recommendation

1. **Schedule bearing inspection** within 7 days
2. Order replacement bearings (SKF 6205-2RS)
3. Increase monitoring frequency

> **Note:** This analysis is based on ISO 10816-3 Zone B/C boundary at 4.5 mm/s.

\`\`\`json
{
  "assetTag": "C-201",
  "vibration_rms": 4.2,
  "status": "alert"
}
\`\`\`
`;
